import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { KpiCard } from "@/components/KpiCard";
import {
  Boxes, PackagePlus, Truck, AlertTriangle, ClipboardList, Cog, Ban, ArrowRight,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip, PieChart, Pie, Legend,
  ComposedChart, Line, CartesianGrid,
} from "recharts";

const PALETTE = ["#065f46", "#b45309", "#1e40af", "#7c2d12", "#3f6212", "#581c87", "#155e75", "#9f1239"];
const AX = { stroke: "#78716c", fontSize: 11 };

function Panel({ title, sub, children, testid, className = "" }) {
  return (
    <div className={`panel p-4 ${className}`} data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="data-label">{title}</div>
        {sub && <div className="text-[11px] text-stone-400">{sub}</div>}
      </div>
      {children}
    </div>
  );
}

const Empty = () => <div className="text-stone-400 text-sm py-12 text-center">No data</div>;

function ActivityChart({ data }) {
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#e7e5e4" vertical={false} />
        <XAxis dataKey="month" {...AX} />
        <YAxis {...AX} tickFormatter={(v) => fmtNumber(v, 0)} />
        <Tooltip formatter={(v) => fmtNumber(v, 0)} />
        <Legend wrapperStyle={{ fontSize: 11 }} />
        <Bar dataKey="received" name="Received" fill="#b45309" radius={[2, 2, 0, 0]} />
        <Bar dataKey="processed" name="Processed" fill="#065f46" radius={[2, 2, 0, 0]} />
        <Bar dataKey="dispatched" name="Dispatched" fill="#1e40af" radius={[2, 2, 0, 0]} />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function ProcessedM3Chart({ data }) {
  if (!data.length) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={260}>
      <ComposedChart data={data} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
        <CartesianGrid stroke="#e7e5e4" vertical={false} />
        <XAxis dataKey="month" {...AX} />
        <YAxis {...AX} tickFormatter={(v) => fmtNumber(v, 0)} />
        <Tooltip formatter={(v) => `${fmtNumber(v, 1)} m³`} />
        <Bar dataKey="processed_m3" name="Processed m³ (by us, incl cut)" fill="#155e75" radius={[2, 2, 0, 0]} />
        <Line type="monotone" dataKey="processed_m3" name="Trend" stroke="#b45309" strokeWidth={2} dot={{ r: 3 }} legendType="none" />
      </ComposedChart>
    </ResponsiveContainer>
  );
}

function Fulfilment({ rows }) {
  if (!rows.length) return <div className="text-stone-400 text-sm py-8 text-center">No open orders</div>;
  return (
    <div className="space-y-3" data-testid="dash-fulfilment-list">
      {rows.map((o) => {
        const req = o.required || 1;
        const disp = Math.min(100, (o.dispatched / req) * 100);
        const alloc = Math.min(100 - disp, (o.allocated / req) * 100);
        return (
          <Link to="/orders" key={o.order_number} className="block hover:bg-stone-50 rounded-sm px-1 py-1" data-testid={`dash-fulfilment-${o.order_number}`}>
            <div className="flex items-baseline justify-between text-xs mb-1">
              <div><span className="font-mono font-semibold text-stone-900">{o.order_number}</span><span className="text-stone-500 ml-2">{o.customer}</span></div>
              <div className="font-mono text-stone-600">{fmtNumber(o.allocated + o.dispatched, 0)} / {fmtNumber(o.required, 0)}</div>
            </div>
            <div className="h-2.5 w-full bg-stone-100 rounded-sm overflow-hidden flex">
              <div className="bg-blue-800 h-full" style={{ width: `${disp}%` }} title={`Dispatched ${o.dispatched}`} />
              <div className="bg-emerald-700 h-full" style={{ width: `${alloc}%` }} title={`Allocated ${o.allocated}`} />
            </div>
          </Link>
        );
      })}
      <div className="flex gap-4 text-[10px] text-stone-500 pt-1">
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 bg-blue-800" /> Dispatched</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 bg-emerald-700" /> Allocated</span>
        <span className="inline-flex items-center gap-1"><i className="inline-block w-2.5 h-2.5 bg-stone-200" /> Still to allocate</span>
      </div>
    </div>
  );
}

function TariffInputs({ t }) {
  if (!t) return null;
  const s = t.settings || {}, dv = t.derived || {}, vol = t.volumes || {};
  const Item = ({ label, value, strong }) => (
    <div><span className="text-stone-500">{label}</span><div className={`font-mono font-semibold ${strong ? "text-emerald-900" : ""}`}>{value}</div></div>
  );
  return (
    <div className="panel p-4" data-testid="dash-tariff-inputs">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="data-label">Tariff Model — Inputs & Derived</div>
          <div className="text-[11px] text-stone-400">Logisticon per m³ · read-only snapshot</div>
        </div>
        <Link to="/cost-dashboard" className="text-xs text-emerald-800 hover:underline inline-flex items-center gap-1" data-testid="dash-tariff-link">Open Cost Dashboard <ArrowRight className="h-3 w-3" /></Link>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-6 gap-y-3 text-xs">
        <Item label="Fixed monthly fee (setting)" value={fmtCurrency(s.fixed_fee_monthly)} />
        <Item label="Conversion (kg per m³)" value={fmtNumber(s.kg_per_m3, 0)} />
        <Item label="Norm output (m³/mo)" value={fmtNumber(s.norm_output_m3, 0)} />
        <Item label={`Fixed fee avg (${dv.fee_months_tagged || 0} tagged mo)`} value={fmtCurrency(dv.fixed_fee_avg)} strong />
        <Item label="Fee at norm output, per m³" value={fmtCurrency(dv.fee_per_m3_at_norm)} strong />
        <Item label={`Norm variable per m³ ${dv.variable_is_auto ? "(auto)" : "(set)"}`} value={fmtCurrency(dv.norm_variable_per_m3)} />
        <Item label="Cost price per m³" value={fmtCurrency(dv.cost_price_per_m3)} strong />
        <Item label="Cost price per ton" value={fmtCurrency(dv.cost_price_per_ton)} />
        <Item label="Finished stock on site" value={`${fmtNumber(vol.finished_stock_m3)} m³`} />
        <Item label="Raw / WIP (indicative)" value={`${fmtNumber(vol.raw_wip_m3)} m³`} />
        <Item label="Processed by us to date" value={`${fmtNumber(vol.own_processed_m3_total)} m³`} strong />
        <Item label="Average CBM per pole" value={`${fmtNumber(vol.avg_cbm_per_pole, 4)} m³`} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [ov, setOv] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(console.error);
    api.get("/dashboard/overview").then((r) => setOv(r.data)).catch(console.error);
  }, []);

  if (!data) {
    return <AppLayout><div className="text-stone-500 font-mono text-sm">Loading dashboard...</div></AppLayout>;
  }

  const s = data.stock;
  const o = data.orders;
  const bd = data.breakdowns;

  return (
    <AppLayout>
      <PageHeader title="Operations Dashboard" subtitle="Live overview of stock, processing, orders and dispatch." />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Total Stock On Hand" value={fmtNumber(s.on_hand.qty)} sub={`${fmtNumber(s.on_hand.cbm, 3)} CBM · ${fmtNumber(s.on_hand.weight)} kg`} icon={<Boxes className="h-5 w-5" />} testid="kpi-stock-on-hand" to="/stock?section=all" />
        <KpiCard label="Available" value={fmtNumber(s.available.qty)} sub={`${s.available.count} bundles · ${fmtNumber(s.available.cbm, 3)} CBM`} accent="primary" icon={<PackagePlus className="h-5 w-5" />} testid="kpi-available" to="/stock?section=available" />
        <KpiCard label="Allocated to Orders" value={fmtNumber(s.allocated.qty)} sub={`${s.allocated.count} bundles · ${fmtNumber(s.allocated.cbm, 3)} CBM`} accent="blue" icon={<ClipboardList className="h-5 w-5" />} testid="kpi-allocated" to="/stock?section=allocated" />
        <KpiCard label="Dispatched" value={fmtNumber(s.dispatched.qty)} sub={`${s.dispatched.count} bundles`} accent="amber" icon={<Truck className="h-5 w-5" />} testid="kpi-dispatched" to="/dispatch" />
        <KpiCard label="Processing WIP" value={fmtNumber(s.processing_wip.qty + s.pending_processing.qty)} sub={`${s.pending_processing.qty} pending · ${s.processing_wip.qty} in progress`} accent="amber" icon={<Cog className="h-5 w-5" />} testid="kpi-processing-wip" to="/processing" />
        <KpiCard label="Damaged" value={fmtNumber(s.damaged.qty)} sub={`${s.damaged.count} awaiting salvage`} accent="red" icon={<AlertTriangle className="h-5 w-5" />} testid="kpi-damaged" to="/damages" />
        <KpiCard label="Written Off" value={fmtNumber(s.written_off?.qty || 0)} sub={`${s.written_off?.count || 0} not salvageable · ${fmtNumber(s.written_off?.cbm || 0, 3)} CBM`} accent="red" icon={<Ban className="h-5 w-5" />} testid="kpi-written-off" to="/damages" />
        <KpiCard label="Open Orders" value={fmtNumber(o.open)} sub={`${o.total} total orders`} accent="blue" icon={<ClipboardList className="h-5 w-5" />} testid="kpi-open-orders" to="/orders" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel title="Monthly Activity — poles" sub="received · processed · dispatched" testid="dash-activity-chart" className="xl:col-span-2">
          {ov ? <ActivityChart data={ov.activity} /> : <Empty />}
        </Panel>
        <Panel title="Open Orders — fulfilment" sub={`${ov?.fulfilment?.length || 0} open`} testid="dash-fulfilment">
          {ov ? <Fulfilment rows={ov.fulfilment} /> : <Empty />}
        </Panel>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 mb-4">
        <Panel title="Processed m³ by month" sub="by us, incl cut — matches tariff model" testid="dash-processed-chart" className="xl:col-span-2">
          {ov ? <ProcessedM3Chart data={ov.activity} /> : <Empty />}
        </Panel>
        <Panel title="Stock on hand by length" sub="available + allocated" testid="dash-length-chart">
          {!ov || !ov.by_length.length ? <Empty /> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={ov.by_length} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#e7e5e4" vertical={false} />
                <XAxis dataKey="length" {...AX} tickFormatter={(v) => `${v}m`} />
                <YAxis {...AX} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip formatter={(v, n) => n === "cbm" ? `${fmtNumber(v, 1)} m³` : fmtNumber(v, 0)} labelFormatter={(v) => `${v} m`} />
                <Bar dataKey="qty" name="Poles" fill="#3f6212" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>

      <div className="mb-4"><TariffInputs t={ov?.tariff} /></div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Panel title="Stock by Origin" testid="dash-origin-chart">
          {!bd.by_origin?.length ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={bd.by_origin}>
                <XAxis dataKey="origin" {...AX} /><YAxis {...AX} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip formatter={(v, n) => n === "cbm" ? `${fmtNumber(v, 1)} m³` : fmtNumber(v, 0)} />
                <Bar dataKey="qty" name="Poles" radius={[2, 2, 0, 0]}>
                  {bd.by_origin.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Stock by Butt Size" sub="top 8 · mm" testid="dash-butt-chart">
          {!bd.by_butt_size?.length ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={bd.by_butt_size}>
                <XAxis dataKey="butt_size" {...AX} /><YAxis {...AX} tickFormatter={(v) => fmtNumber(v, 0)} />
                <Tooltip formatter={(v) => fmtNumber(v, 0)} labelFormatter={(v) => `⌀ ${v} mm`} />
                <Bar dataKey="qty" name="Poles" fill="#065f46" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title="Stock by Colour" testid="dash-colour-chart">
          {!bd.by_colour?.length ? <Empty /> : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={bd.by_colour} dataKey="qty" nameKey="colour" innerRadius={40} outerRadius={70} cx="40%">
                  {bd.by_colour.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                </Pie>
                <Legend layout="vertical" align="right" verticalAlign="middle" wrapperStyle={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => fmtNumber(v, 0)} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Panel>
      </div>
    </AppLayout>
  );
}
